/**
 * Deterministic free-kick physics engine.
 *
 * `simulate()` is the single source of truth — both client (live render) and
 * server (Step 4 verification) call it. Same inputs ⇒ same outputs because
 * we use a fixed 240 Hz timestep internally.
 *
 * Outcome taxonomy follows brief Section 3.3:
 *   GOAL  — ball crosses goal line within frame, keeper does not reach
 *   SAVED — ball crosses within frame, keeper's catch radius covers it
 *   POST  — ball clips frame within ±5 cm tolerance (post or crossbar)
 *   OVER  — ball crosses goal-line plane above the crossbar
 *   WIDE  — ball crosses goal-line plane left/right of the posts, OR ball
 *           hits ground / runs out of time before reaching the goal line
 *   WALL  — ball intersects the defensive wall before reaching the goal
 */

import {
  AIM_ANGLE_RANGE_RAD,
  FRAME_TOLERANCE_M,
  G,
  K_DRAG,
  K_MAGNUS,
  K_WIND,
  LAUNCH_ELEVATION_RAD,
  NEAR_POST_FRAC,
  SCORE_BASE,
  SCORE_CORNER_BONUS,
  SIM_DT,
  SIM_MAX_TIME,
  SPEED_MAX,
  SPEED_MIN,
  SPIN_MAX,
  TOP_HEIGHT_FRAC,
  TRAJECTORY_SAMPLE_EVERY,
} from "./constants";
import type {
  AimInput,
  BallState,
  Outcome,
  OutcomeDetail,
  PuzzleParams,
  SimResult,
  Vec3,
} from "./types";

// ----- vector helpers -----
function v3(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}
function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}
function scale(a: Vec3, s: number): Vec3 {
  return { x: a.x * s, y: a.y * s, z: a.z * s };
}
function mag(a: Vec3): number {
  return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
}

// ----- input → initial conditions -----
export function initialState(input: AimInput, params: PuzzleParams): BallState {
  const speed = SPEED_MIN + (SPEED_MAX - SPEED_MIN) * input.power;
  const horiz = input.angle * AIM_ANGLE_RANGE_RAD;
  const elev = LAUNCH_ELEVATION_RAD;
  const cosE = Math.cos(elev);
  return {
    position: v3(params.ball.x, params.ball.y, 0),
    velocity: v3(
      speed * Math.sin(horiz) * cosE,
      speed * Math.sin(elev),
      speed * Math.cos(horiz) * cosE,
    ),
    spin: input.curl * SPIN_MAX,
    t: 0,
  };
}

export function clampInput(raw: AimInput): AimInput {
  const c = (n: number, lo: number, hi: number): number =>
    Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : 0));
  return {
    angle: c(raw.angle, -1, 1),
    power: c(raw.power, 0, 1),
    curl: c(raw.curl, -1, 1),
  };
}

// ----- one fixed-timestep step -----
export function step(s: BallState, params: PuzzleParams): BallState {
  const v = s.velocity;
  const speed = mag(v);
  const aDrag = scale(v, -K_DRAG * speed);
  const aGrav = v3(0, -G, 0);
  const aWind = scale(params.wind, K_WIND);
  const aMagnus = v3(K_MAGNUS * s.spin * v.z, 0, -K_MAGNUS * s.spin * v.x);
  const a = add(add(aDrag, aGrav), add(aWind, aMagnus));
  const newV = add(v, scale(a, SIM_DT));
  const newP = add(s.position, scale(newV, SIM_DT));
  return { position: newP, velocity: newV, spin: s.spin, t: s.t + SIM_DT };
}

// ----- collision & outcome -----

export function keeperPosAt(t: number, k: PuzzleParams["keeper"]): { x: number; y: number } {
  if (t <= k.reactionTime) return k.restPos;
  if (t >= k.reactionTime + k.diveDuration) return k.diveTo;
  const u = (t - k.reactionTime) / k.diveDuration;
  const e = u * u * (3 - 2 * u);
  return {
    x: k.restPos.x + (k.diveTo.x - k.restPos.x) * e,
    y: k.restPos.y + (k.diveTo.y - k.restPos.y) * e,
  };
}

function hitsWall(prev: BallState, curr: BallState, p: PuzzleParams): null | { x: number; y: number } {
  if (prev.position.z >= p.wall.distance || curr.position.z < p.wall.distance) return null;
  const u = (p.wall.distance - prev.position.z) / (curr.position.z - prev.position.z);
  const x = prev.position.x + (curr.position.x - prev.position.x) * u;
  const y = prev.position.y + (curr.position.y - prev.position.y) * u;
  const halfW = (p.wall.players * 0.5) / 2;
  if (Math.abs(x) <= halfW && y >= 0 && y <= p.wall.height) return { x, y };
  return null;
}

function crossedGoalLine(
  prev: BallState,
  curr: BallState,
  p: PuzzleParams,
): null | { x: number; y: number } {
  if (prev.position.z >= p.goal.distance || curr.position.z < p.goal.distance) return null;
  const u = (p.goal.distance - prev.position.z) / (curr.position.z - prev.position.z);
  return {
    x: prev.position.x + (curr.position.x - prev.position.x) * u,
    y: prev.position.y + (curr.position.y - prev.position.y) * u,
  };
}

function caughtByKeeper(
  cross: { x: number; y: number },
  t: number,
  p: PuzzleParams,
): boolean {
  const kp = keeperPosAt(t, p.keeper);
  const dx = cross.x - kp.x;
  const dy = cross.y - kp.y;
  return Math.sqrt(dx * dx + dy * dy) <= p.keeper.reach;
}

/** Categorize a goal-line crossing into GOAL / SAVED / POST / OVER / WIDE. */
function categorizeCrossing(
  cross: { x: number; y: number },
  t: number,
  p: PuzzleParams,
): { outcome: Outcome; detail: OutcomeDetail | null } {
  const halfW = p.goal.width / 2;
  const TOL = FRAME_TOLERANCE_M;

  // Frame hit?
  const nearLeft = Math.abs(cross.x + halfW) <= TOL && cross.y >= 0 && cross.y <= p.goal.height + TOL;
  const nearRight = Math.abs(cross.x - halfW) <= TOL && cross.y >= 0 && cross.y <= p.goal.height + TOL;
  const nearBar = Math.abs(cross.y - p.goal.height) <= TOL && Math.abs(cross.x) <= halfW + TOL;
  if (nearLeft) return { outcome: "POST", detail: "left_post" };
  if (nearRight) return { outcome: "POST", detail: "right_post" };
  if (nearBar) return { outcome: "POST", detail: "crossbar" };

  // Above crossbar?
  if (cross.y > p.goal.height) return { outcome: "OVER", detail: null };

  // Outside the posts?
  if (cross.x < -halfW) return { outcome: "WIDE", detail: "wide_left" };
  if (cross.x > halfW) return { outcome: "WIDE", detail: "wide_right" };

  // In frame — keeper catches?
  if (caughtByKeeper(cross, t, p)) {
    return { outcome: "SAVED", detail: cross.x < 0 ? "dived_left" : "dived_right" };
  }

  // Goal! Detail by location.
  const isHigh = cross.y >= p.goal.height * TOP_HEIGHT_FRAC;
  const isWide = Math.abs(cross.x) >= halfW * NEAR_POST_FRAC;
  let detail: OutcomeDetail;
  if (isHigh && isWide) detail = "top_bin";
  else if (isHigh) detail = "top_centre";
  else if (isWide) detail = "near_post";
  else detail = "centre";
  return { outcome: "GOAL", detail };
}

function scoreFor(crossPt: { x: number; y: number }, p: PuzzleParams): number {
  const cornerY = p.goal.height;
  const cornerX = (p.goal.width / 2) * (crossPt.x >= 0 ? 1 : -1);
  const dx = crossPt.x - cornerX;
  const dy = crossPt.y - cornerY;
  const diag = Math.sqrt(p.goal.width * p.goal.width + p.goal.height * p.goal.height);
  const proximity = 1 - Math.min(1, Math.sqrt(dx * dx + dy * dy) / diag);
  return Math.round(SCORE_BASE + SCORE_CORNER_BONUS * proximity);
}

function wallDetail(x: number, p: PuzzleParams): OutcomeDetail {
  const halfW = (p.wall.players * 0.5) / 2;
  if (x < -halfW * 0.4) return "wall_left";
  if (x > halfW * 0.4) return "wall_right";
  return "wall_centre";
}

// ----- top-level simulator -----
export function simulate(rawInput: AimInput, params: PuzzleParams): SimResult {
  const input = clampInput(rawInput);
  let state = initialState(input, params);
  const trajectory: Vec3[] = [state.position];
  let stepIdx = 0;

  while (state.t < SIM_MAX_TIME) {
    const next = step(state, params);
    stepIdx += 1;
    if (stepIdx % TRAJECTORY_SAMPLE_EVERY === 0) trajectory.push(next.position);

    // Wall before goal.
    const wallHit = hitsWall(state, next, params);
    if (wallHit) {
      return {
        outcome: "WALL",
        outcomeDetail: wallDetail(wallHit.x, params),
        score: 0,
        endPosition: { ...next.position },
        hitTime: next.t,
        trajectory,
      };
    }

    // Goal-line crossing.
    const cross = crossedGoalLine(state, next, params);
    if (cross) {
      const cat = categorizeCrossing(cross, next.t, params);
      const score = cat.outcome === "GOAL" ? scoreFor(cross, params) : 0;
      return {
        outcome: cat.outcome,
        outcomeDetail: cat.detail,
        score,
        endPosition: { x: cross.x, y: cross.y, z: params.goal.distance },
        hitTime: next.t,
        trajectory,
      };
    }

    // Ground hit before goal.
    if (next.position.y < 0) {
      return {
        outcome: "WIDE",
        outcomeDetail: "ground",
        score: 0,
        endPosition: { ...next.position },
        hitTime: next.t,
        trajectory,
      };
    }

    state = next;
  }

  // Time-out — never reached the goal.
  return {
    outcome: "WIDE",
    outcomeDetail: "ground",
    score: 0,
    endPosition: { ...state.position },
    hitTime: state.t,
    trajectory,
  };
}

// ----- trajectory sampling for the replay layer -----
export function sampleTrajectory(trajectory: Vec3[], elapsedSec: number): Vec3 {
  if (trajectory.length === 0) return { x: 0, y: 0, z: 0 };
  const sampleHz = 1 / (TRAJECTORY_SAMPLE_EVERY * SIM_DT);
  const idx = elapsedSec * sampleHz;
  if (idx <= 0) return trajectory[0]!;
  if (idx >= trajectory.length - 1) return trajectory[trajectory.length - 1]!;
  const i0 = Math.floor(idx);
  const i1 = i0 + 1;
  const t = idx - i0;
  const a = trajectory[i0]!;
  const b = trajectory[i1]!;
  return {
    x: a.x + (b.x - a.x) * t,
    y: a.y + (b.y - a.y) * t,
    z: a.z + (b.z - a.z) * t,
  };
}
